/**
 * Language context & hook for i18n.
 *
 * Usage:
 *   1. Wrap the app in <LanguageProvider lang={langFromLoader}>
 *   2. In any component:
 *        const { lang, setLang, t } = useLanguage();
 *        <span>{t(auth).login}</span>          // picks auth.HEB or auth.ENG
 *        <span>{auth[lang].login}</span>        // equivalent manual access
 */

import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import type { Language } from "~/utils/language";
import { DEFAULT_LANGUAGE, writeLanguageCookie } from "~/utils/language";
import Spinner from "~/components/common/Spinner";
import { layout } from "~/translations/layout/layout";

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface LanguageContextValue {
  /** Current active language key ("HEB" | "ENG"). */
  lang: Language;
  /** Switch language – writes cookie and reloads for SSR dir change. */
  setLang: (lang: Language) => void;
  /** True while a language switch is in progress (cookie written, reload pending). */
  isSwitchingLanguage: boolean;
  /**
   * Helper: given a translation object like `{ HEB: {...}, ENG: {...}, RUS: {...} }`,
   * returns the branch that matches the current language.
   * Fallback chain: requested lang → ENG → HEB (DEFAULT_LANGUAGE).
   */
  t: <T extends Record<string, any>>(translations: T) => T[Language];
}

const LanguageContext = createContext<LanguageContextValue>({
  lang: DEFAULT_LANGUAGE,
  setLang: () => {},
  isSwitchingLanguage: false,
  t: (translations) => translations[DEFAULT_LANGUAGE],
});

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface LanguageProviderProps {
  /** Language determined on the server (from cookie). */
  lang: Language;
  children: ReactNode;
}

export function LanguageProvider({ lang, children }: LanguageProviderProps) {
  const [isSwitchingLanguage, setIsSwitchingLanguage] = useState(false);

  const setLang = useCallback((newLang: Language) => {
    if (newLang === lang) return;
    setIsSwitchingLanguage(true);
    // Defer reload to allow React to render the spinner overlay first.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        writeLanguageCookie(newLang);
        // Reload so entry.server.tsx picks up the new cookie and
        // renders the correct `dir` / `lang` on the <html> element.
        window.location.reload();
      });
    });
  }, [lang]);

  const t = useCallback(
    <T extends Record<string, any>>(translations: T): T[Language] => {
      return translations[lang] ?? translations["ENG"] ?? translations[DEFAULT_LANGUAGE];
    },
    [lang]
  );

  const layoutT = layout[lang] ?? layout["ENG"] ?? layout[DEFAULT_LANGUAGE];

  return (
    <LanguageContext.Provider value={{ lang, setLang, isSwitchingLanguage, t }}>
      {children}
      {isSwitchingLanguage && (
        <div
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-3 bg-white/90 dark:bg-gray-900/90"
          role="status"
          aria-live="polite"
          aria-label={layoutT.switchingLanguage ?? layoutT.loading}
        >
          <Spinner size="large" />
          <span className="text-sm text-gray-600 dark:text-gray-400">{layoutT.switchingLanguage ?? layoutT.loading}</span>
        </div>
      )}
    </LanguageContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useLanguage() {
  return useContext(LanguageContext);
}
